import React, { useEffect, useState, useRef } from 'react';
import { Progress, List, Tag, Typography, Card, Space, Button, Alert } from 'antd';
import {
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  ExpandOutlined,
  ShrinkOutlined,
  ReloadOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { adminTasksApi } from '../../services/api';
import { AdminTask, AdminTaskStatus, AdminSubtask } from '../../types';

const { Text } = Typography;

interface AdminTaskTrackerProps {
  taskId: string;
  onComplete?: (task: AdminTask) => void;
  onFail?: (task: AdminTask) => void;
  onUpdate?: (task: AdminTask) => void;
  onRestart?: () => Promise<void>;
  onDelete?: () => Promise<void>;
  title?: string;
  pollingInterval?: number;
  floating?: boolean;
}

export const AdminTaskTracker: React.FC<AdminTaskTrackerProps> = ({
  taskId,
  onComplete,
  onFail,
  onUpdate,
  onRestart,
  onDelete,
  title = 'Tiến độ thực hiện',
  pollingInterval = 3000,
  floating = false
}) => {
  const [task, setTask] = useState<AdminTask | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pollKey, setPollKey] = useState(0);

  // Use refs for callbacks and state to avoid re-triggering useEffect and multiple calls
  const onCompleteRef = useRef(onComplete);
  const onFailRef = useRef(onFail);
  const onUpdateRef = useRef(onUpdate);
  const hasCalledCallbackRef = useRef(false);
  
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onFailRef.current = onFail;
    onUpdateRef.current = onUpdate;
  }, [onComplete, onFail, onUpdate]);

  useEffect(() => {
    hasCalledCallbackRef.current = false;
    setTask(null);
    setLoading(true);
  }, [taskId]);

  useEffect(() => {
    let intervalId: any;
    let isMounted = true;
    let isProcessing = false;
    let isPaused = false;

    const poll = async () => {
      if (isProcessing || hasCalledCallbackRef.current || isPaused) return;
      isProcessing = true;
      
      try {
        const response = await adminTasksApi.getTaskDetail(taskId);
        if (!isMounted) return;
        
        const updatedTask = response.data;
        setTask(updatedTask);
        onUpdateRef.current?.(updatedTask);
        
        const isTerminal = updatedTask.status === AdminTaskStatus.COMPLETED
          || updatedTask.status === AdminTaskStatus.COMPLETED_WITH_WARNINGS
          || updatedTask.status === AdminTaskStatus.FAILED;

        if (isTerminal) {
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          
          if (!hasCalledCallbackRef.current) {
            hasCalledCallbackRef.current = true;
            // Only call callback if we actually transitioned to this state OR if it's the first check and it's already terminal
            // To prevent loops, we ensure this ref is truly stable.
            if (updatedTask.status === AdminTaskStatus.COMPLETED) {
              onCompleteRef.current?.(updatedTask);
            } else {
              onFailRef.current?.(updatedTask);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch task detail:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
          isProcessing = false;
        }
      }
    };

    poll();
    intervalId = setInterval(poll, pollingInterval);

    // Pause polling khi tab bị ẩn, resume và poll ngay khi tab active trở lại
    const handleVisibilityChange = () => {
      if (document.hidden) {
        isPaused = true;
      } else {
        isPaused = false;
        poll(); // poll ngay lập tức khi tab active lại
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [taskId, pollingInterval, pollKey]);

  const handleRestart = async () => {
    if (!onRestart) return;
    setRestarting(true);
    try {
      await onRestart();
      hasCalledCallbackRef.current = false;
      setPollKey(k => k + 1);
    } finally {
      setRestarting(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  const getStatusIcon = (status: AdminTaskStatus) => {
    switch (status) {
      case AdminTaskStatus.COMPLETED:
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case AdminTaskStatus.COMPLETED_WITH_WARNINGS:
        return <CheckCircleOutlined style={{ color: '#faad14' }} />;
      case AdminTaskStatus.IN_PROGRESS:
        return <SyncOutlined spin style={{ color: '#1890ff' }} />;
      case AdminTaskStatus.FAILED:
        return <CloseCircleOutlined style={{ color: '#f5222d' }} />;
      default:
        return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />;
    }
  };

  const getStatusTag = (status: AdminTaskStatus) => {
    switch (status) {
      case AdminTaskStatus.COMPLETED:
        return <Tag color="success">HOÀN THÀNH</Tag>;
      case AdminTaskStatus.COMPLETED_WITH_WARNINGS:
        return <Tag color="warning">HOÀN THÀNH (CÓ LỖI)</Tag>;
      case AdminTaskStatus.IN_PROGRESS:
        return <Tag color="processing" icon={<SyncOutlined spin />}>ĐANG XỬ LÝ</Tag>;
      case AdminTaskStatus.FAILED:
        return <Tag color="error">THẤT BẠI</Tag>;
      default:
        return <Tag color="default">ĐANG CHỜ</Tag>;
    }
  };

  const containerStyle: React.CSSProperties = floating ? {
    position: 'fixed',
    top: 80,
    right: 24,
    zIndex: 1000,
    width: collapsed ? 300 : 400,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    transition: 'all 0.3s ease'
  } : { marginBottom: 24 };

  if (loading && !task) {
    return (
      <div style={containerStyle}>
        <Card size="small" title={title}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <LoadingOutlined style={{ fontSize: 24 }} spin />
            <div style={{ marginTop: 10 }}>Đang tải...</div>
          </div>
        </Card>
      </div>
    );
  }

  if (!task) return null;

  const { progress, subtasks, error } = task.detail;

  return (
    <div style={containerStyle}>
      <Card 
        size="small"
        title={
          <Space>
            {collapsed ? getStatusIcon(task.status) : null}
            <Text strong>{title}</Text>
            {collapsed ? <Text type="secondary">{progress}%</Text> : getStatusTag(task.status)}
          </Space>
        }
        extra={
          <Button 
            type="text" 
            size="small" 
            icon={collapsed ? <ExpandOutlined /> : <ShrinkOutlined />} 
            onClick={() => setCollapsed(!collapsed)} 
          />
        }
        bodyStyle={collapsed ? { display: 'none' } : { maxHeight: '400px', overflowY: 'auto' }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text type="secondary">Tiến độ tổng thể</Text>
            <Text strong>{progress}%</Text>
          </div>
          <Progress 
            percent={progress} 
            size="small" 
            status={task.status === AdminTaskStatus.FAILED ? 'exception' : (task.status === AdminTaskStatus.COMPLETED ? 'success' : 'active')} 
          />
        </div>

        {(task.status === AdminTaskStatus.FAILED || task.status === AdminTaskStatus.COMPLETED_WITH_WARNINGS) && (
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
            {error && (
              <Alert
                message={error}
                type={task.status === AdminTaskStatus.COMPLETED_WITH_WARNINGS ? 'warning' : 'error'}
                showIcon
              />
            )}
            <Space style={{ width: '100%' }}>
              {onRestart && (
                <Button
                  type="primary"
                  danger
                  icon={<ReloadOutlined />}
                  loading={restarting}
                  onClick={handleRestart}
                  size="small"
                  style={{ flex: 1 }}
                >
                  Thử lại
                </Button>
              )}
              {onDelete && (
                <Button
                  icon={<DeleteOutlined />}
                  loading={deleting}
                  onClick={handleDelete}
                  size="small"
                  style={{ flex: 1 }}
                >
                  Xóa task
                </Button>
              )}
            </Space>
          </Space>
        )}

        <List
          size="small"
          header={<Text strong style={{ fontSize: '12px' }}>Các công đoạn:</Text>}
          dataSource={subtasks}
          renderItem={(subtask: AdminSubtask) => {
            const result = (subtask as any).result;
            const phases: any[] = result?.phases || [];
            const panelErrors: any[] = result?.panelErrors || [];
            return (
              <List.Item style={{ padding: '8px 0', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                  <div style={{ marginRight: 8 }}>{getStatusIcon(subtask.status)}</div>
                  <div style={{ flex: 1 }}>
                    <Text style={{ fontSize: '13px', fontWeight: 600 }}>{subtask.name}</Text>
                    {subtask.message && (
                      <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                        {subtask.message}
                      </Text>
                    )}
                  </div>
                  {subtask.status === AdminTaskStatus.IN_PROGRESS && (
                    <LoadingOutlined spin style={{ color: '#1890ff', marginLeft: 8 }} />
                  )}
                </div>

                {/* Phases */}
                {phases.length > 0 && (
                  <div style={{ marginLeft: 24, marginTop: 4, width: '100%' }}>
                    {phases.map((phase: any) => (
                      <div key={phase.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: '10px' }}>{getStatusIcon(phase.status)}</span>
                        <Text style={{ fontSize: '11px' }} type={phase.status === AdminTaskStatus.FAILED ? 'danger' : 'secondary'}>
                          {phase.name}
                          {phase.total != null && ` (${phase.total - (phase.failed || 0)}/${phase.total}${phase.failed ? `, ${phase.failed} lỗi` : ''})`}
                        </Text>
                      </div>
                    ))}
                  </div>
                )}

                {/* Panel errors */}
                {panelErrors.length > 0 && (
                  <div style={{ marginLeft: 24, marginTop: 4, width: '100%' }}>
                    <Text style={{ fontSize: '11px', color: '#f5222d', fontWeight: 600 }}>
                      ⚠ {panelErrors.length} panel lỗi:
                    </Text>
                    <div style={{ maxHeight: 80, overflowY: 'auto' }}>
                      {panelErrors.map((pe: any, idx: number) => (
                        <div key={idx}>
                          <Text style={{ fontSize: '10px', color: '#ff7875' }}>
                            • {pe.label}: {pe.error}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </List.Item>
            );
          }}
        />
      </Card>
    </div>
  );
};
